import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTeam } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Target, User, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TeamDetailPage = () => {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await getTeam(teamId);
        setTeam(response.data);
      } catch (error) {
        console.error('Error fetching team:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, [teamId]);

  const getPhotoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API}/api/files/${path}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4">
        <div className="container-max">
          <div className="animate-pulse">
            <div className="h-8 bg-white/10 rounded w-1/4 mb-8" />
            <div className="h-12 bg-white/10 rounded w-1/2 mb-4" />
          </div>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-4">
        <div className="container-max text-center">
          <h1 className="text-2xl font-heading font-bold mb-4">Mannschaft nicht gefunden</h1>
          <Link to="/mannschaften" className="text-primary hover:underline">Zurück</Link>
        </div>
      </div>
    );
  }

  // Keep original order (first added = first shown)
  const sortedPlayers = team.players || [];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="team-detail-page">
      {/* Header */}
      <section className="section-padding bg-surface">
        <div className="container-max">
          <Link to="/mannschaften" className="inline-flex items-center gap-2 text-text-secondary hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Zurück zu Mannschaften
          </Link>
          <div className="flex items-center gap-6">
            {team.photo_url ? (
              <img src={getPhotoUrl(team.photo_url)} alt={team.name} className="w-28 h-28 object-contain rounded-xl bg-white/5 p-3" />
            ) : (
              <div className="w-28 h-28 bg-primary/10 rounded-xl flex items-center justify-center">
                <Target className="w-14 h-14 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter">
                {team.name}
              </h1>
              {team.league && (
                <Badge variant="outline" className="border-accent text-accent mt-2 text-sm">
                  {team.league}
                </Badge>
              )}
              {team.description && (
                <p className="text-text-secondary mt-3 max-w-2xl">{team.description}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Players - Football Club Style */}
      <section className="section-padding">
        <div className="container-max">
          <h2 className="text-2xl font-heading font-bold uppercase tracking-tighter mb-8">
            Spieler ({sortedPlayers.length})
          </h2>

          {sortedPlayers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sortedPlayers.map((player) => {
                const fn = player.team_function || '';
                const isCapt = fn.toLowerCase() === 'kapitän';
                const hasFunction = fn.trim() !== '';
                return (
                  <Card
                    key={player.player_id}
                    className={`card-surface overflow-hidden transition-all duration-300 hover:border-primary/40 ${isCapt ? 'ring-1 ring-yellow-500/30' : ''}`}
                    data-testid={`player-card-${player.player_id}`}
                  >
                    <CardContent className="p-0">
                      {/* Player Photo - Large */}
                      <div className="relative aspect-[3/4] bg-gradient-to-b from-white/5 to-transparent">
                        {player.photo_url ? (
                          <img
                            src={getPhotoUrl(player.photo_url)}
                            alt={player.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-20 h-20 text-white/10" />
                          </div>
                        )}
                        {hasFunction && (
                          <div className={`absolute top-3 right-3 px-2 py-1 rounded-sm flex items-center gap-1 ${
                            isCapt ? 'bg-yellow-500 text-black' :
                            fn === 'Vize-Kapitän' ? 'bg-gray-300 text-black' :
                            fn === 'Trainer' ? 'bg-red-600 text-white' :
                            'bg-primary/80 text-white'
                          }`} data-testid={`function-badge-${player.player_id}`}>
                            {isCapt && <Shield className="w-3.5 h-3.5" />}
                            <span className="text-xs font-bold uppercase">{fn}</span>
                          </div>
                        )}
                      </div>

                      {/* Player Info */}
                      <div className="p-5">
                        <h3 className="text-lg font-heading font-bold uppercase mb-3">{player.name}</h3>
                        <div className="space-y-1.5">
                          {player.bio && (
                            <p className="text-text-secondary text-xs mt-2 line-clamp-2">{player.bio}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 card-surface rounded-lg">
              <User className="w-16 h-16 text-text-secondary mx-auto mb-4" />
              <p className="text-text-secondary">Noch keine Spieler in dieser Mannschaft.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TeamDetailPage;
